import { api } from '../../lib/api.js';
import { getAppConfig } from '../../lib/app-config.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html, nothing } from '../../lib/lit.js';
import {
    buildPollPayload,
    canEditClosesAt,
    canEditContent,
    closeConsequence,
    draftToPreview,
    emptyDraft,
    INTRO_MAX,
    isDirty,
    MAX_QUESTIONS,
    moveQuestion,
    newQuestion,
    PROMPT_MAX,
    pollToDraft,
    portalPollUrl,
    RESULTS_VISIBILITIES,
    STATE_BADGE,
    TITLE_MAX,
} from '../../lib/polls.js';
import { router } from '../../lib/router.js';
import '../app/markdown-input.js';
import '../app/share-link.js';
import './poll-preview.js';

// Write or edit one poll: a title, a Markdown intro, the two per-poll settings,
// an optional close time, and an ordered list of questions.
//
// The whole poll saves at once — d3-community has no per-question endpoint —
// so questions live here until Save. Opening, closing and deleting are separate
// steps from saving, because each is a moment that cannot be taken back:
// opening locks the questions and tells every member, closing an at-close poll
// releases its results to respondents. Each is offered only on a saved poll
// with no unsaved changes, and asks once more inline (Bootstrap's modal JS
// cannot reach into a shadow root).
class PollEditorView extends BaseComponent {
    static properties = {
        /** The poll being edited; absent for a new poll. */
        pollId: { type: String, attribute: 'poll-id' },
        _loading: { state: true },
        _loadError: { state: true },
        /** @type {object|null} The poll as last saved, or null before the first save. */
        _saved: { state: true },
        _responseCount: { state: true },
        /** @type {ReturnType<typeof emptyDraft>} */
        _draft: { state: true },
        _saving: { state: true },
        /** @type {string[]} */
        _errors: { state: true },
        _notice: { state: true },
        /** @type {'open'|'close'|'delete'|null} */
        _confirming: { state: true },
        _acting: { state: true },
        _previewing: { state: true },
        _uploading: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 56rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .options-input {
            font-family: ui-monospace, monospace;
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this.pollId = '';
        this._loading = true;
        this._loadError = '';
        this._saved = null;
        this._responseCount = 0;
        this._draft = emptyDraft();
        this._saving = false;
        this._errors = [];
        this._notice = '';
        this._confirming = null;
        this._acting = false;
        this._previewing = false;
        this._uploading = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            if (this.pollId) {
                const body = await api.get(`/polls/${this.pollId}`);
                this._applySaved(body.poll, body.responseCount);
            }
        } catch (error) {
            this._loadError = error.status === 404 ? 'No such poll.' : error.message;
        } finally {
            this._loading = false;
        }
    }

    /** Adopt a poll as returned by the API as both the saved and the edited state. */
    _applySaved(poll, responseCount) {
        this._saved = poll;
        if (responseCount !== undefined) this._responseCount = responseCount;
        this._draft = pollToDraft(poll);
    }

    get _dirty() {
        return isDirty(this._saved, this._draft);
    }

    get _busy() {
        return this._saving || this._acting || this._uploading > 0;
    }

    _patch(patch) {
        this._draft = { ...this._draft, ...patch };
    }

    _patchQuestion(index, patch) {
        const questions = this._draft.questions.map((q, i) =>
            i === index ? { ...q, ...patch } : q,
        );
        this._patch({ questions });
    }

    _addQuestion(kind) {
        this._patch({ questions: [...this._draft.questions, newQuestion(kind)] });
    }

    _removeQuestion(index) {
        this._patch({ questions: this._draft.questions.filter((_, i) => i !== index) });
    }

    _moveQuestion(index, delta) {
        const questions = moveQuestion(this._draft.questions, index, delta);
        if (questions) this._patch({ questions });
    }

    async _save(event) {
        event.preventDefault();
        this._notice = '';
        this._confirming = null;

        const { payload, errors } = buildPollPayload(this._draft);
        this._errors = errors;
        if (!payload) return;

        this._saving = true;
        try {
            if (this._saved) {
                const body = await api.put(`/polls/${this._saved.id}`, payload);
                this._applySaved(body.poll, body.responseCount);
                this._notice = this._saved.state === 'draft' ? 'Draft saved.' : 'Saved.';
            } else {
                const body = await api.post('/polls', payload);
                this._applySaved(body.poll, body.responseCount);
                this._notice = 'Draft saved. It is not on the portal until you open it.';
                // Give the new poll its own URL without re-rendering the view (a
                // router navigation would swap in a fresh editor and drop state).
                window.history.replaceState({}, '', `/polls/${this._saved.id}`);
            }
        } catch (error) {
            this._errors = [error.message];
        } finally {
            this._saving = false;
        }
    }

    async _open() {
        await this._transition(async () => {
            const body = await api.post(`/polls/${this._saved.id}/open`);
            this._applySaved(body.poll);
            this._notice =
                'Opened. Members have been notified and can answer on the portal — the link to share is above.';
        });
    }

    async _close() {
        await this._transition(async () => {
            const body = await api.post(`/polls/${this._saved.id}/close`);
            this._applySaved(body.poll);
            this._notice =
                body.poll.resultsVisibility === 'at_close'
                    ? 'Closed. Respondents have been notified that the results are up.'
                    : 'Closed.';
        });
    }

    async _delete() {
        await this._transition(async () => {
            await api.delete(`/polls/${this._saved.id}`);
            router.navigate('/polls');
        });
    }

    async _transition(change) {
        this._acting = true;
        this._errors = [];
        try {
            await change();
            this._confirming = null;
        } catch (error) {
            this._errors = [error.message];
        } finally {
            this._acting = false;
        }
    }

    render() {
        if (this._loading) {
            return html`<div class="wrap"><p class="text-muted">Loading…</p></div>`;
        }
        if (this._loadError) {
            return html`<div class="wrap">
                <div class="alert alert-danger" role="alert">${this._loadError}</div>
                <a href="/polls">All polls</a>
            </div>`;
        }

        const saved = this._saved;
        return html`
            <div class="wrap">
                <a class="small" href="/polls"><i class="bi bi-arrow-left"></i> Polls</a>
                <div class="d-flex align-items-center gap-2 mt-2 mb-3 flex-wrap">
                    <h1 class="h4 mb-0">${saved ? 'Edit poll' : 'New poll'}</h1>
                    ${saved ? html`<span class="badge ${STATE_BADGE[saved.state]}">${saved.state}</span>` : nothing}
                    ${
                        saved && saved.state !== 'draft'
                            ? html`<span class="ms-auto d-inline-flex align-items-center gap-3">
                                  <d3-share-link
                                      href=${portalPollUrl(getAppConfig().communityPortalUrl, saved.id)}
                                  ></d3-share-link>
                                  <a class="small" href="/polls/${saved.id}/results">
                                      <i class="bi bi-bar-chart"></i>
                                      ${this._responseCount}
                                      ${this._responseCount === 1 ? 'response' : 'responses'}
                                  </a>
                              </span>`
                            : nothing
                    }
                </div>

                ${this._renderStateNotice()}

                <div class="btn-group btn-group-sm mb-3" role="group" aria-label="Edit or preview">
                    <button
                        class="btn ${this._previewing ? 'btn-outline-secondary' : 'btn-secondary'}"
                        type="button"
                        @click=${() => {
                            this._previewing = false;
                        }}
                    >
                        <i class="bi bi-pencil"></i> Edit
                    </button>
                    <button
                        class="btn ${this._previewing ? 'btn-secondary' : 'btn-outline-secondary'}"
                        type="button"
                        @click=${() => {
                            this._previewing = true;
                        }}
                    >
                        <i class="bi bi-eye"></i> Preview
                    </button>
                </div>

                <form @submit=${this._save}>
                    ${this._previewing ? this._renderPreview() : this._renderFields()}
                    ${this._renderMessages()}
                    ${this._renderButtons()}
                </form>
            </div>
        `;
    }

    _renderStateNotice() {
        const saved = this._saved;
        if (!saved || saved.state === 'draft') return nothing;
        if (saved.state === 'open') {
            return html`<div class="alert alert-info" role="status">
                This poll is open. Its questions are locked — changing them after responses arrive
                would corrupt the tallies. Only the close time can still change.
            </div>`;
        }
        return html`<div class="alert alert-secondary" role="status">
            This poll is closed. Nothing about it can change.
        </div>`;
    }

    _renderPreview() {
        return html`
            <p class="text-muted small">How members will see this poll on the community portal.</p>
            <d3-poll-preview .poll=${draftToPreview(this._draft)}></d3-poll-preview>
        `;
    }

    _renderFields() {
        const draft = this._draft;
        const contentLocked = !canEditContent(this._saved);
        const closesAtLocked = !canEditClosesAt(this._saved);
        const disabled = contentLocked || this._busy;
        return html`
            <div class="mb-3">
                <label class="form-label" for="title">Title</label>
                <input
                    id="title"
                    class="form-control"
                    type="text"
                    maxlength=${TITLE_MAX}
                    .value=${draft.title}
                    ?disabled=${disabled}
                    @input=${(e) => this._patch({ title: e.target.value })}
                />
            </div>

            <div class="mb-3">
                <d3-markdown-input
                    input-id="intro"
                    label="Introduction"
                    rows="6"
                    maxlength=${INTRO_MAX}
                    placeholder="What this poll is for, in Markdown. Optional."
                    .value=${draft.intro}
                    ?disabled=${disabled}
                    @value-changed=${(e) => this._patch({ intro: e.detail.value })}
                    @uploading-changed=${this._onUploading}
                ></d3-markdown-input>
            </div>

            <fieldset class="mb-4">
                <legend class="h6">Settings</legend>
                <div class="row g-3">
                    <div class="col-md-6">
                        <div class="form-label">Results are shown</div>
                        ${RESULTS_VISIBILITIES.map(
                            (v) => html`
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        name="resultsVisibility"
                                        id="visibility-${v.value}"
                                        value=${v.value}
                                        .checked=${draft.resultsVisibility === v.value}
                                        ?disabled=${disabled}
                                        @change=${() => this._patch({ resultsVisibility: v.value })}
                                    />
                                    <label class="form-check-label" for="visibility-${v.value}">
                                        ${v.label}
                                        <span class="form-text d-block mt-0">${v.help}</span>
                                    </label>
                                </div>
                            `,
                        )}
                    </div>
                    <div class="col-md-6">
                        <div class="form-label">Answers</div>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="allowChanges"
                                .checked=${draft.allowChanges}
                                ?disabled=${disabled}
                                @change=${(e) => this._patch({ allowChanges: e.target.checked })}
                            />
                            <label class="form-check-label" for="allowChanges">
                                Respondents may change their answers until the poll closes
                                <span class="form-text d-block mt-0"
                                    >Otherwise answers are final once submitted.</span
                                >
                            </label>
                        </div>
                        <label class="form-label mt-3" for="closesAt">Closes</label>
                        <input
                            id="closesAt"
                            class="form-control"
                            type="datetime-local"
                            .value=${draft.closesAt}
                            ?disabled=${closesAtLocked || this._busy}
                            @input=${(e) => this._patch({ closesAt: e.target.value })}
                        />
                        <div class="form-text">
                            The poll closes itself at this time. Leave blank to close it by hand.
                        </div>
                    </div>
                </div>
            </fieldset>

            <div class="d-flex align-items-center gap-2 mb-2">
                <h2 class="h6 mb-0">Questions</h2>
                <span class="text-muted small">${draft.questions.length} of ${MAX_QUESTIONS}</span>
            </div>
            ${
                draft.questions.length === 0
                    ? html`<p class="text-muted small">No questions yet. Add one below.</p>`
                    : nothing
            }
            ${draft.questions.map((question, index) => this._renderQuestion(question, index, disabled))}
            ${
                contentLocked
                    ? nothing
                    : html`<div class="d-flex gap-2 mb-4">
                          <button
                              class="btn btn-sm btn-outline-primary"
                              type="button"
                              ?disabled=${this._busy || draft.questions.length >= MAX_QUESTIONS}
                              @click=${() => this._addQuestion('choice')}
                          >
                              <i class="bi bi-plus-lg"></i> Multiple choice
                          </button>
                          <button
                              class="btn btn-sm btn-outline-primary"
                              type="button"
                              ?disabled=${this._busy || draft.questions.length >= MAX_QUESTIONS}
                              @click=${() => this._addQuestion('text')}
                          >
                              <i class="bi bi-plus-lg"></i> Free text
                          </button>
                      </div>`
            }
        `;
    }

    _renderQuestion(question, index, disabled) {
        const last = this._draft.questions.length - 1;
        return html`
            <div class="card mb-3">
                <div class="card-body">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <span class="fw-semibold">Question ${index + 1}</span>
                        <select
                            class="form-select form-select-sm w-auto"
                            aria-label="Question type"
                            .value=${question.kind}
                            ?disabled=${disabled}
                            @change=${(e) => this._patchQuestion(index, { kind: e.target.value })}
                        >
                            <option value="choice" ?selected=${question.kind === 'choice'}>Multiple choice</option>
                            <option value="text" ?selected=${question.kind === 'text'}>Free text</option>
                        </select>
                        ${
                            disabled
                                ? nothing
                                : html`<div class="btn-group btn-group-sm ms-auto" role="group">
                                      <button
                                          class="btn btn-outline-secondary"
                                          type="button"
                                          title="Move up"
                                          ?disabled=${index === 0}
                                          @click=${() => this._moveQuestion(index, -1)}
                                      >
                                          <i class="bi bi-arrow-up"></i>
                                      </button>
                                      <button
                                          class="btn btn-outline-secondary"
                                          type="button"
                                          title="Move down"
                                          ?disabled=${index === last}
                                          @click=${() => this._moveQuestion(index, 1)}
                                      >
                                          <i class="bi bi-arrow-down"></i>
                                      </button>
                                      <button
                                          class="btn btn-outline-danger"
                                          type="button"
                                          title="Remove"
                                          @click=${() => this._removeQuestion(index)}
                                      >
                                          <i class="bi bi-x-lg"></i>
                                      </button>
                                  </div>`
                        }
                    </div>

                    <div class="mb-3">
                        <d3-markdown-input
                            input-id="prompt-${index}"
                            label="Prompt"
                            rows="3"
                            maxlength=${PROMPT_MAX}
                            placeholder="The question, in Markdown."
                            .value=${question.prompt}
                            ?disabled=${disabled}
                            @value-changed=${(e) => this._patchQuestion(index, { prompt: e.detail.value })}
                            @uploading-changed=${this._onUploading}
                        ></d3-markdown-input>
                    </div>

                    ${question.kind === 'choice' ? this._renderChoiceFields(question, index, disabled) : nothing}
                </div>
            </div>
        `;
    }

    _renderChoiceFields(question, index, disabled) {
        return html`
            <div class="mb-3">
                <label class="form-label" for="options-${index}">Options</label>
                <textarea
                    id="options-${index}"
                    class="form-control options-input"
                    rows="4"
                    placeholder="One option per line"
                    .value=${question.options}
                    ?disabled=${disabled}
                    @input=${(e) => this._patchQuestion(index, { options: e.target.value })}
                ></textarea>
                <div class="form-text">One per line, in order. At least two before the poll can open.</div>
            </div>
            <div class="d-flex gap-4 flex-wrap">
                <div class="form-check">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="multiple-${index}"
                        .checked=${question.allowMultiple}
                        ?disabled=${disabled}
                        @change=${(e) => this._patchQuestion(index, { allowMultiple: e.target.checked })}
                    />
                    <label class="form-check-label" for="multiple-${index}">Pick any that apply</label>
                </div>
                <div class="form-check">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="notlisted-${index}"
                        .checked=${question.allowNotListed}
                        ?disabled=${disabled}
                        @change=${(e) => this._patchQuestion(index, { allowNotListed: e.target.checked })}
                    />
                    <label class="form-check-label" for="notlisted-${index}">
                        Offer "Not listed", which takes a written answer
                    </label>
                </div>
            </div>
        `;
    }

    _onUploading = (event) => {
        this._uploading = Math.max(0, this._uploading + (event.detail.uploading ? 1 : -1));
    };

    _renderMessages() {
        return html`
            ${
                this._errors.length > 0
                    ? html`<div class="alert alert-danger" role="alert">
                          ${this._errors.map((message) => html`<div>${message}</div>`)}
                      </div>`
                    : nothing
            }
            ${
                this._notice
                    ? html`<div class="alert alert-success" role="status">${this._notice}</div>`
                    : nothing
            }
        `;
    }

    _renderButtons() {
        const saved = this._saved;
        const state = saved?.state ?? 'draft';
        if (state === 'closed') return nothing;

        return html`
            <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                <button
                    class="btn btn-primary"
                    type="submit"
                    ?disabled=${this._busy}
                    title=${this._uploading > 0 ? 'Wait for the image to finish uploading' : ''}
                >
                    ${this._saving ? 'Saving…' : state === 'draft' ? 'Save draft' : 'Save'}
                </button>
                ${this._renderActions()}
            </div>
        `;
    }

    _renderActions() {
        const saved = this._saved;
        if (!saved) return nothing;

        if (this._confirming && !this._dirty) {
            const { question, label, busyLabel, action } = {
                open: {
                    question: 'Open this poll? Its questions lock and every member is notified.',
                    label: 'Open now',
                    busyLabel: 'Opening…',
                    action: this._open,
                },
                close: {
                    question: `Close this poll? ${closeConsequence(saved)}`,
                    label: 'Close now',
                    busyLabel: 'Closing…',
                    action: this._close,
                },
                delete: {
                    question: 'Delete this draft? It cannot be recovered.',
                    label: 'Delete',
                    busyLabel: 'Deleting…',
                    action: this._delete,
                },
            }[this._confirming];
            return html`
                <span class="ms-auto small">${question}</span>
                <button class="btn btn-danger" type="button" ?disabled=${this._acting} @click=${action}>
                    ${this._acting ? busyLabel : label}
                </button>
                <button
                    class="btn btn-outline-secondary"
                    type="button"
                    ?disabled=${this._acting}
                    @click=${() => {
                        this._confirming = null;
                    }}
                >
                    Cancel
                </button>
            `;
        }

        const dirtyTitle = this._dirty ? 'Save your changes first' : '';
        const confirm = (what) => () => {
            this._notice = '';
            this._confirming = what;
        };
        if (saved.state === 'draft') {
            return html`
                <button
                    class="btn btn-outline-danger ms-auto"
                    type="button"
                    ?disabled=${this._dirty || this._busy}
                    title=${dirtyTitle}
                    @click=${confirm('open')}
                >
                    Open…
                </button>
                <button
                    class="btn btn-outline-secondary"
                    type="button"
                    ?disabled=${this._dirty || this._busy}
                    title=${dirtyTitle}
                    @click=${confirm('delete')}
                >
                    Delete draft…
                </button>
            `;
        }
        return html`
            <button
                class="btn btn-outline-danger ms-auto"
                type="button"
                ?disabled=${this._dirty || this._busy}
                title=${dirtyTitle}
                @click=${confirm('close')}
            >
                Close…
            </button>
        `;
    }
}

customElements.define('d3-poll-editor-view', PollEditorView);
